<template>
  <v-flex class="fill-content">
    <div v-if="orderLines.length">
      <div v-for="(orderLine, i) in orderLines" :key="i">
        <order-line
          v-if="!orderLine.parentOrderLine"
          :orderLine="orderLine"
          :orderLines="orderLines"
          :isPaid="isPaid"
          :firstOrderLine="i === orderLines.indexOf(orderLines.filter(o => !o.parentOrderLine)[0])"
          :currencies="currencies"
        />
      </div>
    </div>
    <v-alert :value="someFreeProduct && !isPaid" text color="info-text" class="info mx-3" transition="scale-transition" mode="in-out">
      <v-layout align-start>
        <v-icon class="mr-2 mt-1">mdi-alert-circle-outline</v-icon>
        <span class="free-price-alert">{{ $t('activity_reservation.warning.some_product_included') }}</span>
      </v-layout>
    </v-alert>
    <v-divider />
    <!-- SUBTOTAL -->
    <v-flex v-if="amountAndCurrency.amount && serviceChargesToShow.length" pb-2 pt-6>
      <v-layout ml-7 mr-5 align-end justify-center>
        <span class="subtotal-text">{{$t('activity_reservation.subtotal')}}</span>
        <v-spacer/>
        <v-layout column align-end justify-start>
          <span v-if="showDisplayCurrency" class="display-currency__amount-text">
            {{ getPriceString({ amountAndCurrency: amountAndCurrency, displayCurrency: displayCurrency }).split(' ')[0] }}
            <span class="display-currency__currency-text">
              {{ getPriceString({ amountAndCurrency: amountAndCurrency, displayCurrency: displayCurrency }).split(' ')[1] }}
            </span>
          </span>
          <span class="currency__amount-text">
            {{ getPriceString({ amountAndCurrency: amountAndCurrency }).split(' ')[0] }}
            <span class="currency__currency-text">
              {{ getPriceString({ amountAndCurrency: amountAndCurrency }).split(' ')[1] }}
            </span>
          </span>
        </v-layout>
      </v-layout>
    </v-flex>
    <!-- SERVICE CHARGE -->
    <div v-if="serviceChargesToShow.length" class="pb-6 remove-padding-from-last">
      <v-flex v-for="(charge, index) in serviceChargesToShow" :key="index" :pb-2="index + 1 < serviceChargesToShow.length" >
        <v-layout align-center ml-7 mr-5>
          <span class="subtotal-text">{{$t('activity_reservation.service_charge', {serviceCharge: charge.value})}}</span>
          <v-spacer/>
          <v-layout column align-end justify-start>
            <span 
              v-if="showDisplayCurrency" 
              class="display-currency__amount-text"
            >
              {{ getPriceString({ amountAndCurrency: {amount:charge.amount, currency:charge.currency}, displayCurrency }).split(' ')[0] }}
              <span class="display-currency__currency-text">
                {{ getPriceString({ amountAndCurrency: {amount:charge.amount, currency:charge.currency}, displayCurrency }).split(' ')[1] }}
              </span>
            </span>
            <span class="currency__amount-text">
              {{ getPriceString({ amountAndCurrency: {amount:charge.amount, currency:charge.currency} }).split(' ')[0] }}
              <span class="currency__currency-text">
                {{ getPriceString({ amountAndCurrency: {amount:charge.amount, currency:charge.currency} }).split(' ')[1] }}
              </span>
            </span>
          </v-layout>
        </v-layout>
      </v-flex>
    </div>
    <!-- TOTAL -->
    <v-flex v-if="amountAndCurrency && amountAndCurrency.amount" class="order-total">
      <v-container justify-end align-end fill-height row class="ma-0">
        <v-layout v-if="showDisplayCurrency" class="flex-none display-currency" justify-end>
          <div class="display-currency-total__amount-text">{{ getPriceString({ amountAndCurrency: totalPrice, displayCurrency: displayCurrency }).split(' ')[0] }}</div>
          <div class="display-currency-total__currency-text">{{ getPriceString({ amountAndCurrency: totalPrice, displayCurrency: displayCurrency }).split(' ')[1] }}</div>
        </v-layout>
        <v-flex xs12>
          <v-layout v-if="amountAndCurrency.amount" align-end>
            <span class="totals-text">{{$t('activity_reservation.total')}}</span>
            <v-spacer/>
            <span class="amount-text">
              {{ getPriceString({ amountAndCurrency: totalPrice }).split(' ')[0] }}
              <span class="currency-text">
                {{ getPriceString({ amountAndCurrency: totalPrice }).split(' ')[1] }}
              </span>
            </span>
          </v-layout>
        </v-flex>
      </v-container>
    </v-flex>
  </v-flex>
</template>

<script>
import { mapGetters } from 'vuex'

import { orderLineItemEntities, orderLineTypes } from '@/config'

import moneyMixins from '@/mixins/MoneyFormat'

export default {
  props: {
    orderLines: Array,
    totalByCurrencies: Array,
    isPaid: Boolean,
    currencies: Array,
  },
  mixins: [
    moneyMixins,
  ],
  components: {
    OrderLine: () => import('@/components/view-components/reservations/ActivityReservationOrderLine.vue'),
  },
  computed: {
    ...mapGetters('me', ['getPriceString', 'showDisplayCurrency', 'displayCurrency']),
    amountAndCurrency () {
      return this.totalByCurrencies[0]
    },
    someFreeProduct () {
      const { PRODUCT, CATALOG_PRODUCT_MODIFIER_OPTION } = orderLineItemEntities
      
      return !!this.orderLines.filter(({ id, itemEntity, price }) => {
        if (itemEntity === PRODUCT) {
          const modifiers = this.orderLines.filter(({ itemEntity, parentOrderLine, includedOnParentLine }) => {
            return itemEntity === CATALOG_PRODUCT_MODIFIER_OPTION && parentOrderLine === id && !includedOnParentLine
          })

          const totalPriceModifiers = modifiers.reduce((total, modifier) => {
            return total + modifier.price[0].amount
          }, 0)

          return !(price[0].amount + totalPriceModifiers)
        }

        return false
      }).length
    },
    serviceChargesOrderLines () {
      const serviceCharges = this.orderLines.filter((orderLine) => orderLine.orderLineType === orderLineTypes.SERVICE_CHARGE && orderLine.price[0].amount)
      return serviceCharges
    },
    serviceChargesToShow () {
      const setOfValues = new Set()
      const valuesOfServiceCharges = {}
      for (let serviceCharge of this.serviceChargesOrderLines) {
        const { value, quantity, price } = serviceCharge
        const { amount, currency } = price[0]
        if (setOfValues.has(value)) {
          valuesOfServiceCharges[value].amount += amount * quantity
        }
        else {
          setOfValues.add(value)
          valuesOfServiceCharges[value] = {
            amount: amount * quantity,
            currency,
            value,
          }
        }
      }
      return Object.values(valuesOfServiceCharges)
    },
    totalPrice () {
      const { amount: subtotal, currency } = this.totalByCurrencies[0]
      const total = this.serviceChargesToShow.reduce(
        (acc, serviceCharge) => acc + serviceCharge.amount,
        subtotal
      )
      return { amount: total, currency }
    },
    serviceChargesOrderLines () {
      const serviceCharges = this.orderLines.filter((orderLine) => orderLine.orderLineType === orderLineTypes.SERVICE_CHARGE && orderLine.price[0].amount)
      return serviceCharges
    },
    serviceChargesToShow () {
      const setOfValues = new Set()
      const valuesOfServiceCharges = {}
      for (let serviceCharge of this.serviceChargesOrderLines) {
        const { value, quantity, price } = serviceCharge
        const { amount, currency } = price[0]
        if (setOfValues.has(value)) {
          valuesOfServiceCharges[value].amount += amount * quantity
        }
        else {
          setOfValues.add(value)
          valuesOfServiceCharges[value] = {
            amount: amount * quantity,
            currency,
            value,
          }
        }
      }
      return Object.values(valuesOfServiceCharges)
    },
  },
}
</script>

<style lang="scss" scoped>
.fill-content {
  margin-left: -24px;
  margin-right: -24px;
  min-width: min(100vw, 600px) !important;
}
.subtotal-text {
  color: var(--v-dark-text-base);
  font-family: 'Mukta Mahee';
  font-size: 13px;
  font-style: normal;
  font-weight: 500;
  line-height: normal;
  letter-spacing: 0.25px;
}

.order-total {
  color: var(--v-primary-lighten1);
  background-color: rgba(var(--v-primary-lighten1-rgb), 0.1);
  padding-left: 28px;
  padding-right: 20px;
  padding-bottom: 10px;
  padding-top: 19px;
  font-family: 'Mukta Mahee';
  font-size: 28px;
  font-style: normal;
  font-weight: 700;
  line-height: normal;
  letter-spacing: 0.25px;
}
.bold-text {
  font-weight: 500 !important;
}
.fix-height {
  height: 15px;
}
.currency-text {
  font-size: 16px;
  font-style: normal;
  font-weight: 700;
  line-height: normal;
  letter-spacing: 0.225px;
}
.display-currency {
  margin-bottom: -7px;
}.currency__amount-text {
  color: var(--v-dark-text-base);
  text-align: right;
  font-family: 'Mukta';
  font-size: 16px;
  font-style: normal;
  font-weight: 500;
  line-height: 16px; /* 87.5% */
  letter-spacing: 0.22px;
}
.currency__currency-text {
  color: var(--v-dark-text-base);
  font-family: 'Mukta';
  font-size: 10px;
  font-style: normal;
  font-weight: 500;
  line-height: 16px;
  letter-spacing: 0.22px;
}
.display-currency__amount-text {
  color: var(--v-dark-text-base);
  text-align: right;
  font-family: 'Mukta';
  font-size: 12px;
  font-style: normal;
  font-weight: 500;
  line-height: 12px; /* 66.667% */
  letter-spacing: 0.22px;
}
.display-currency__currency-text {
  color: var(--v-dark-text-base);
  font-family: 'Mukta';
  font-size: 10px;
  font-style: normal;
  font-weight: 500;
  line-height: 12px;
  letter-spacing: 0.22px;
}
.display-currency-total__amount-text {
  color: var(--v-primary-lighten1);
  text-align: right;
  font-family: 'Roboto';
  font-size: 12px;
  font-style: normal;
  font-weight: 400;
  line-height: 12px;
  letter-spacing: 0.25px;
  margin-right: 5px;
}
.display-currency-total__currency-text {
  color: var(--v-primary-lighten1);
  font-family: 'Roboto';
  font-size: 11px;
  font-style: normal;
  font-weight: 400;
  letter-spacing: 0.25px;
}
.service-charge-text {
  font-size: 12px;
}
.color-text {
  color: rgba(0, 0, 0, 0.6);
}
::v-deep {
  .free-price-alert {
    font-weight: 500 !important;
    color: rgba(0, 0, 0, 0.6);
  }
}
</style>
